/**
 * Decodes common HTML entities to their character equivalents.
 */
function decodeHtmlEntities(text: string): string {
  const entityMap: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&#39;': "'",
    '&apos;': "'",
    '&nbsp;': ' ',
    '&auml;': '\u00e4',
    '&ouml;': '\u00f6',
    '&uuml;': '\u00fc',
    '&Auml;': '\u00c4',
    '&Ouml;': '\u00d6',
    '&Uuml;': '\u00dc',
    '&szlig;': '\u00df',
    '&ndash;': '\u2013',
    '&mdash;': '\u2014',
    '&euro;': '\u20ac',
    '&copy;': '\u00a9',
    '&reg;': '\u00ae',
    '&middot;': '\u00b7',
    '&hellip;': '\u2026',
    '&laquo;': '\u00ab',
    '&raquo;': '\u00bb',
  };

  let result = text;

  // Named entities
  Object.entries(entityMap).forEach(([entity, char]) => {
    result = result.split(entity).join(char);
  });

  // Numeric entities (&#123; and &#x1A;)
  result = result.replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)));
  result = result.replace(/&#x([0-9a-fA-F]+);/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)));

  return result;
}

/**
 * Converts HTML email content to plain text for editing.
 *
 * - `</p><p>` and `<br/>` / `<br>` become newlines
 * - Remaining HTML tags are stripped
 * - HTML entities are decoded to real characters
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';

  let text = html;

  // Paragraph boundaries -> double newline (before stripping tags)
  text = text.replace(/<\/p>\s*<p[^>]*>/gi, '\n\n');

  // Line breaks -> single newline
  text = text.replace(/<br\s*\/?>/gi, '\n');

  // Strip remaining HTML tags
  text = text.replace(/<[^>]+>/g, '');

  // Decode HTML entities
  text = decodeHtmlEntities(text);

  // Trim leading/trailing whitespace
  return text.trim();
}

/**
 * Converts plain text back to simple HTML for email sending.
 *
 * - Double newline (empty line) -> new paragraph (`<p>...</p>`)
 * - Single newline within a paragraph -> `<br/>`
 */
export function plainTextToHtml(plainText: string): string {
  if (!plainText) return '';

  // Split into paragraphs on double newlines
  const paragraphs = plainText.split(/\n{2,}/);

  return paragraphs
    .map((paragraph) => {
      // Escape HTML special characters so user text is safe
      const inner = paragraph
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br/>');
      return `<p>${inner}</p>`;
    })
    .join('');
}
